import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as PropTypes from 'prop-types';

import StatusMessagesPortal from 'as-ui-kit/components/StatusMessagesPortal';

import {DataContext, LocaleContext, LoadingContext, ScrollsContext} from 'contexts';
import { goLogin, goToBilling } from 'utils/http';
import {
    searchGeo,
    getCitiesByIds,
    getRegionsByIds,
    getCountriesByIds
} from 'utils/requests/geo';
import * as vandamus from 'utils/requests/search';
import * as scrollsApi from 'utils/requests/scrolls';
import { getCarTypes } from 'utils/requests/carTypes';
import * as iconsApi from 'utils/requests/icons';
import { entities} from 'logic/entities';
import { eventMetrics } from 'logic/requestParams';
import {mergeWithDefault, sortByName, sortByType} from './logic/scrolls';
import { makeGeoRequest, makeGeo, makeLicense, makeProfile, makeMetricEventRequest } from 'logic/adapters';
import PayPopup from 'components/Page/PayPopup';
import BreadcrumbsLinks from 'components/BreadcrumbsLinks';
import Page from 'components/Page';
import { openWindow } from 'utils/window';

import i18nStrings from './strings';
import nanoid from 'nanoid';
import {sendMetric} from 'utils/metrics';
import {createBrowserRouter, RouterProvider} from 'react-router-dom';
import {apiUrl, glossaryUrl, mainSiteUrl} from 'urls';
import { PeriodType } from 'consts/consts';
import SelectedFirmsActionsBanner from 'components/SelectedFirmsActionsBanner';

const mobileMatchMedia = '(max-width: 640px)';

const App = ({isAtiApp, locale}) => {
    const strings = i18nStrings[locale];
    const [isMobile, setIsMobile] = useState(false);
    const [searchResults, setSearchResults] = useState(null);
    const [searchResultsParams, setSearchResultParams] = useState(null);
    const [icons, setIcons] = useState(null);
    const [licenseInfo, setLicenseInfo] = useState({});
    const [carTypes, setCarTypes] = useState([]);
    const [carTypesArray, setCarTypesArray] = useState([]);
    const [ratingParams, setRatingParams] = useState(null);
    const [loading, setLoading] = useState({});
    const [canUseQueryParams, setCanUseQueryParams] = useState({});
    const [scrolls, setScrolls] = useState(null);
    const [showScrollsSaved, setShowScrollsSaved] = useState(false);
    const [isOutsideRussia, setIsOutsideRussia] = useState(false);
    const [scrolledFirmsIds, setScrolledFirmsIds] = useState([]);

    const searchResultsSelected = searchResults?.result?.filter(r => r.isSelected) || [];

    const isUser = window.atiUser?.is_user || false;
    const userProfile = useMemo(() => {
        if (!isUser) {
            return null;
        }

        return makeProfile(window.atiUser.profile);
    }, [isUser]);

    const mobileMedia = window.matchMedia(mobileMatchMedia);
    const mobileMediaListener = () => setIsMobile(mobileMedia.matches);

    const loadingStarted = useCallback((name) => setLoading(prevState => ({...prevState, [name]: true })), []);
    const loadingFinished = useCallback((name) => setLoading(prevState => ({...prevState, [name]: false })), []);
    const catchLoadingError = useCallback((name, err) => {
        loadingFinished(name);
        throw err;
    }, [loadingFinished]);

    const getIcons = useCallback((atiIds) => {
        if(!isUser || !atiIds?.length){
            return;
        }
        const lang = locale === 'en' ? 'eng' : 'ru';
        iconsApi.getIcons(atiIds, apiUrl, lang)
            .then((result) => {
                setIcons(result);
            });
    }, [ isUser, locale]);

    const getScrolls = useCallback(() => {
        if(!isUser){
            return;
        }
        loadingStarted(entities.scrolls);
        scrollsApi.getScrolls(apiUrl)
            .then((result) => {
                const mergedScrolls = mergeWithDefault(result, locale);
                setScrolls(mergedScrolls.sort((a, b)=>sortByType(a, b) || sortByName(a,b)));
                loadingFinished(entities.scrolls);
            });
    }, [isUser, loadingStarted, loadingFinished, locale]);

    const setVisibleNumbers = useCallback((firms) => {
        let visibleNumber = 1;
        for (let i = 0; i < firms.length; i++) {
            firms[i].key = nanoid();
            firms[i].visibleNumber = firms[i].firmTopType > 0 ? 0 : visibleNumber++;
        }
    }, []);

    const searchTruckers = useCallback((searchParams) => {
        loadingStarted(entities.search);
        setSearchResults({result: null});
        return vandamus.search(searchParams, locale, mainSiteUrl)
            .catch((err) => catchLoadingError(entities.search, err))
            .then((s) => {
                if (s?.result) setVisibleNumbers(s.result);

                setSearchResults(s);
                setSearchResultParams(searchParams);
                setLicenseInfo(makeLicense(s));
                loadingFinished(entities.search);
                getIcons(s?.result?.map(t=>t.id).filter(Boolean));
                getScrolls();
                setScrolledFirmsIds([]);
            });
    }, [locale, catchLoadingError, loadingStarted, loadingFinished, getIcons, getScrolls, setVisibleNumbers]);

    const findCities = useCallback((inputValue) => searchGeo(makeGeoRequest(inputValue, locale), apiUrl), [ locale]);

    const findGeoById = useCallback((geoList) => {
        const citiesIds = [], regionsIds = [], countriesIds = [];
        geoList.forEach(geo => {
            switch (geo.type) {
                case 0:
                    countriesIds.push(geo.id);
                    break;
                case 1:
                    regionsIds.push(geo.id);
                    break;
                case 2:
                    citiesIds.push(geo.id);
                    break;
            }
        });
        let promises = [
            ...(citiesIds.length > 0 ? [getCitiesByIds(citiesIds)] : []),
            ...(regionsIds.length > 0 ? [getRegionsByIds(regionsIds)] : []),
            ...(countriesIds.length > 0 ? [getCountriesByIds(countriesIds)] : []),
        ];
        return Promise.all(promises).then(results => makeGeo(results.reduce((acc, curr) => {
            const key = Object.keys(curr)[0];
            if (key) {
                acc[key] = curr[key];
            }
            return acc;
        }, {})));
    }, []);

    const loadCarTypes = useCallback(() => {
        loadingStarted(entities.carTypes);
        getCarTypes(glossaryUrl).then((dict) => {
            setCarTypesArray(dict);
            let carTypesDict = {};
            dict.forEach((item) =>{
                carTypesDict[item.Id] = item;
            });
            setCarTypes(carTypesDict);
            loadingFinished(entities.carTypes);
            setCanUseQueryParams(prevState => ({
                ...prevState,
                [entities.carTypes]: true
            }));
        });
    }, [loadingStarted, loadingFinished]);

    const updateScrolls = useCallback(async (serverObj)=>{
	    await scrollsApi.inviteToScrolls(serverObj, apiUrl);
	    await getScrolls();
	    setShowScrollsSaved(true);
	    setTimeout(() => setShowScrollsSaved(false), 2000);
    }, [getScrolls]);

    const loadLicenseInfo = useCallback(() => {
        loadingStarted(entities.license);
        vandamus.init(mainSiteUrl)
            .catch((err) => catchLoadingError(entities.license, err))
            .then((s) => {
                setLicenseInfo(makeLicense(s));
                loadingFinished(entities.license);
                setCanUseQueryParams(prevState => ({
                    ...prevState,
                    [entities.license]: true
                }));
            });
    }, [catchLoadingError, loadingStarted, loadingFinished]);

    const loadRatingParams = useCallback((rq) => {
        loadingStarted(entities.ratingParams);
        setRatingParams(null);
        vandamus.getCoefficients(rq, mainSiteUrl)
            .catch((err) => catchLoadingError(entities.ratingParams, err))
            .then((p) => {
                setRatingParams(p);
                loadingFinished(entities.ratingParams);
            });
    }, [catchLoadingError, loadingStarted, loadingFinished]);

    const saveRatingParams = useCallback((rq) => {
        loadingStarted(entities.ratingParams);
        setRatingParams(null);
        vandamus.saveCoefficients(rq, mainSiteUrl)
            .catch((err) => catchLoadingError(entities.ratingParams, err))
            .then((p) => {
                window.appErrno?.show({text: 'Успешно сохранили параметры рейтинга', type: 'success'});
                setRatingParams(p);
                loadingFinished(entities.ratingParams);
            });
    }, [catchLoadingError, loadingStarted, loadingFinished]);

    const writeMetricEvent = useCallback((eventName, targetFirmsIds) => {
        const firms = targetFirmsIds?.map(id => {
            const firmIndex = searchResults?.result?.findIndex(r => r.id === id) ?? null;
            const firm = firmIndex !== null && firmIndex >= 0 ? searchResults.result[firmIndex] : null;

            return {
                id: id,
                rankInSearch: firmIndex + 1,
                firmTopType: firm?.firmTopType || 0
            };
        });
        return vandamus.writeEvent(makeMetricEventRequest(eventName, firms, searchResultsParams?.from,
            searchResultsParams?.to, searchResults?.result?.length, searchResults?.searchId), mainSiteUrl);
    }, [searchResultsParams?.from, searchResultsParams?.to, searchResults?.result, searchResults?.searchId]);


    const topUpVirtualAccount = useCallback((periodType) => {
        if (!userProfile || !licenseInfo.searchAccessPrices) {
            return;
        }
        const topUpAtises = licenseInfo.searchAccessPrices[periodType]?.personal - Math.min(userProfile.balance, 0);
        writeMetricEvent(eventMetrics.insufficientFunds).then(() => goToBilling(topUpAtises));
    }, [userProfile, licenseInfo, writeMetricEvent]);

    const processPay = useCallback((navigate, search, periodType = PeriodType.day) => {
        if (!userProfile) {
            goLogin(isAtiApp);
        } else if (userProfile.balance < 0) {
            topUpVirtualAccount(periodType);
        } else if (licenseInfo.searchAccessPrices) {
            navigate({pathname: '/active-truckers/pay', search });
        }
    }, [userProfile, licenseInfo.searchAccessPrices, isAtiApp, topUpVirtualAccount]);

    const handlePayPopupAccessCheck = useCallback((periodType) => {
        if (!userProfile) {
            goLogin(isAtiApp);
        } else if (userProfile.balance < 0) {
            topUpVirtualAccount(periodType);
        }
    }, [isAtiApp, topUpVirtualAccount, userProfile]);

    const writeTransitionSource = useCallback((transitionSource) => {
        let metricName;
        switch (transitionSource) {
            case 'owncargoes':
                metricName = eventMetrics.transitionFromOwnCargoes;
                break;
            case 'rating':
                metricName = eventMetrics.transitionFromRating;
                break;
            case 'update_popup_free_access':
                metricName = eventMetrics.transitionFromUpdatePopupFreAccess;
                break;
        }

        if (metricName)
            writeMetricEvent(metricName);
    }, [writeMetricEvent]);

    const showPayByCardStatus = useCallback((status) => {
        switch (status) {
            case 'success': {
                sendMetric('active-carriers-successful-payment-by-card');
                window.appErrno?.show({
                    text: strings.successDirectPayment,
                    delay: 5000,
                    type: 'success-old'
                });
                break;
            }
            case 'fail': {
                const text = `${strings.failDirectPayment} <a href='//${mainSiteUrl}/tickets/create' target='_blank' rel='noopener noreferrer' style='color:#FFFFFF;'>${strings.failDirectPaymentLink}</a>`;
                window.appErrno?.show({
                    text: text,
                    delay: 10000,
                    type: 'error-old',
                });
                break;
            }
        }
    },[strings]);

    const openPassport = useCallback((id) => {
        writeMetricEvent(eventMetrics.openPassport, [id]);
        openWindow(`${mainSiteUrl}/firms/${id}/rating`, 865, 800);
    }, [writeMetricEvent]);

    const writeEvent = useCallback((eventName, targetFirmsIds) => {
        writeMetricEvent(eventName, targetFirmsIds);
    }, [writeMetricEvent]);

    const writeScrollFirmsEvent = useCallback(firmsIds => {
        const ids = [...new Set(firmsIds.filter(f => !scrolledFirmsIds.includes(f)))];
        if (ids.length > 0) {
            writeMetricEvent(eventMetrics.scrollFirms, ids);
            setScrolledFirmsIds([...scrolledFirmsIds, ...ids]);
        }
    }, [writeMetricEvent, scrolledFirmsIds]);

    const handleAllSearchResultsSelection = useCallback((isSelected) => {
        setSearchResults(searchResults => {
            const updatedResults = searchResults?.result?.map(ur => ({
                ...ur,
                isSelected: isSelected
            }));
            return { result: updatedResults };
        });
    }, []);

    const handleSearchResultSelection = useCallback((firmId, isSelected) => {
        setSearchResults(searchResults => {
            const firmIndex = searchResults?.result?.findIndex(r => r.id === firmId);
            if (firmIndex !== -1 && firmIndex !== undefined) {
                const updatedResults = [...searchResults.result];
                updatedResults[firmIndex] = {
                    ...updatedResults[firmIndex],
                    isSelected: isSelected
                };
                return { result: updatedResults };
            }
            return searchResults;
        });
    }, []);

    useEffect(() => {
        mobileMediaListener();
        mobileMedia.addListener(mobileMediaListener);

        loadLicenseInfo();
        loadCarTypes();
        loadingStarted(entities.filters);

        return () => {
            mobileMedia.removeListener(mobileMediaListener);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadLicenseInfo, loadCarTypes]);

    useEffect(() => {
        if (typeof window?.setRegistrationPopupCookie === 'function') {
            window.setRegistrationPopupCookie();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [window.setRegistrationPopupCookie]);

    const scrollsLayer = useMemo(()=> {
        return {
            scrolls,
            getScrolls,
            updateScrolls,
            showScrollsSaved
        };
    }, [scrolls, getScrolls, updateScrolls, showScrollsSaved]);

    const dataLayer = useMemo(() => {
        return {
            searchResults,
	        handleSearchResultSelection,
	        handleAllSearchResultsSelection,
            searchResultsParams,
            searchTruckers,
            processPay,
            findCities,
            findGeoById,
            canUseQueryParams,
            setCanUseQueryParams,
            setIsOutsideRussia,
            loadingStarted,
            loadingFinished,
            carTypes,
            carTypesArray,
            icons,
            isUser,
            userProfile,
            isMobile,
            licenseInfo,
            isOutsideRussia,
            writeTransitionSource,
            openPassport,
            writeEvent,
            writeScrollFirmsEvent,
            ratingParams,
            loadRatingParams,
            saveRatingParams,
            showPayByCardStatus
        };
    }, [searchResults, searchResultsParams, searchTruckers, processPay, findCities, findGeoById,
        canUseQueryParams, setCanUseQueryParams, setIsOutsideRussia, loadingStarted, loadingFinished, carTypes, carTypesArray, icons, isUser, userProfile,
        isMobile, licenseInfo, isOutsideRussia, writeTransitionSource, openPassport, writeEvent, writeScrollFirmsEvent, ratingParams, loadRatingParams,
	    saveRatingParams, showPayByCardStatus, handleSearchResultSelection, handleAllSearchResultsSelection]);

    const loadingData = useMemo(() => {
        return {
            carTypesIsLoading: loading[entities.carTypes],
            searchResultsIsLoading: loading[entities.search],
            licenseInfoIsLoading: loading[entities.license],
            filtersIsLoading: loading[entities.filters],
            scrolls: loading[entities.scrolls],
            ratingParamsIsLoading: loading[entities.ratingParams],
            isGlobalLoading: Object.entries(loading)
                .filter(([name])=> name !== entities.scrolls)
                .some(([, isLoading]) => isLoading === true)
        };
    }, [loading]);

    let router = createBrowserRouter([
        {
            path: '/active-truckers/',
            element: (<Page />),
            children: [
                {
                    path: 'pay',
                    element: (
                        <PayPopup
                            topUpVirtualAccount={topUpVirtualAccount}
                            writeMetricEvent={writeMetricEvent}
                            onPayPopupAccessCheck={handlePayPopupAccessCheck}
                        />
                    ),
                },
            ],
        },
    ]);


    return (
        <LoadingContext.Provider value={loadingData}>
            <DataContext.Provider value={dataLayer}>
                <LocaleContext.Provider value={locale}>
                    <BreadcrumbsLinks />
                    <ScrollsContext.Provider value={scrollsLayer}>
                        <RouterProvider router={router}  />
                        <StatusMessagesPortal />
		                {searchResultsSelected.length > 0 && (
			                <SelectedFirmsActionsBanner searchResultsSelected={searchResultsSelected}/>
		                )}
                    </ScrollsContext.Provider>
                </LocaleContext.Provider>
            </DataContext.Provider>
        </LoadingContext.Provider>
    );
};

export default App;

App.propTypes = {
    isAtiApp: PropTypes.bool,
    locale: PropTypes.string,
};
